import { graphql, usePaginationFragment, RefetchFnDynamic } from "react-relay";
import { OperationType } from "relay-runtime";
import { CompetitionLeaderboardFragment$key } from "./__generated__/CompetitionLeaderboardFragment.graphql";
import { FormattedMessage, FormattedDate, FormattedNumber } from "react-intl";
import EntityProfilePic from "./EntityProfilePic";
import { Link } from "react-router";
import LoadMore from "./LoadMore";
import { Ref, forwardRef, useImperativeHandle } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";

const CompetitionLeaderboardFragment = graphql`
  fragment CompetitionLeaderboardFragment on Competition
  @refetchable(queryName: "CompetitionLeaderboardFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    stage: { type: "Int" }
  ) {
    stage(stage: $stage) {
      hasLeaderboard
      leaderboard(after: $cursor, first: $count)
        @connection(key: "CompetitionLeaderboardFragment_leaderboard") {
        edges {
          cursor
          score
          validatedAt
          node {
            entity {
              id
              username
              ...EntityProfilePicFragment @arguments(thumbnail: true)
            }
          }
        }
      }
    }
  }
`;

export type RefetchFn = RefetchFnDynamic<
  OperationType,
  CompetitionLeaderboardFragment$key
>;

interface Props {
  stage: CompetitionLeaderboardFragment$key;
}

export interface CompetitionLeaderboardHandle {
  refetch: RefetchFn;
}

export default forwardRef(function CompetitionLeaderboard(
  props: Props,
  ref: Ref<CompetitionLeaderboardHandle>,
) {
  const {
    data: { stage },
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  } = usePaginationFragment(CompetitionLeaderboardFragment, props.stage);
  useImperativeHandle(ref, () => ({ refetch }));
  if (!stage) {
    return null;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <FormattedMessage defaultMessage="User" />
            </TableHead>
            <TableHead>
              <FormattedMessage defaultMessage="Submitted At" />
            </TableHead>
            {stage.hasLeaderboard && (
              <TableHead>
                <FormattedMessage defaultMessage="Score" />
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {stage.leaderboard.edges.map(
            ({ cursor, node, score, validatedAt }) => (
              <TableRow key={cursor}>
                <TableCell className="whitespace-nowrap py-2 px-3">
                  <Link to={`/${node.entity.username}`}>
                    <div className="flex flex-row items-center hover:cursor-pointer">
                      <div className="pr-4">
                        <EntityProfilePic entity={node.entity} size="12" />
                      </div>
                      <span>{node.entity.username}</span>
                    </div>
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap py-2 px-3 text-center">
                  <FormattedDate
                    value={validatedAt}
                    month="long"
                    day="numeric"
                    year="numeric"
                  />
                </TableCell>
                {stage.hasLeaderboard && (
                  <TableCell className="whitespace-nowrap py-2 px-3 text-center">
                    <FormattedNumber value={score} />
                  </TableCell>
                )}
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      <LoadMore
        loadMore={loadNext}
        hasMore={hasNext}
        isLoading={isLoadingNext}
      />
    </div>
  );
});
